import React, { ReactNode, useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Grid, IconButton } from "@mui/material";
import MaybeTransition from "./MaybeTransition";
import useIsMobile from "../hooks/useIsMobile";
import useIsDesktop from "../hooks/useIsDesktop";
import { PAGE_FRAME_HEIGHT } from "common/constants";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

export enum Panel {
  Left,
  Right,
}

const StyledHeader = ({ children }: { children: ReactNode }) => {
  const isMobile = useIsMobile();
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: "100%",
        display: "inline-flex",
        padding: isMobile ? "12px 20px 0" : "0 20px",
        borderRadius: isMobile ? 0 : "16px 0 0 0",
        background: "white",
      }}
    >
      {children}
    </Box>
  );
};

type PanelProps = {
  content: React.ReactNode;
  title?: React.ReactNode | string;
  subtitle?: React.ReactNode | string;
  switchContent?: React.ReactNode | string;
  sx?: React.CSSProperties;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  customToggleButton?: (
    isCollapsed: boolean,
    onToggle: () => void,
  ) => React.ReactNode;
};

type TwoPanelGridProps = {
  leftPanel: PanelProps;
  rightPanel?: PanelProps;
  defaultPanel?: Panel;
  onChangePanel?: (panel: Panel) => void;
  headerAction?: ReactNode;
  showHeaderAction?: boolean;
  leftPanelMaxWidth?: string;
};
export default ({
  leftPanel,
  rightPanel,
  onChangePanel,
  headerAction,
  showHeaderAction,
  defaultPanel = Panel.Left,
  leftPanelMaxWidth = "950px",
}: TwoPanelGridProps) => {
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [panel, setPanel] = useState<Panel>(defaultPanel);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(
    rightPanel?.defaultCollapsed ?? false,
  );

  const changePanel = (panel: Panel) => {
    setPanel(panel);
    onChangePanel && onChangePanel(panel);
  };

  const toggleRightPanel = () => {
    setRightPanelCollapsed(!rightPanelCollapsed);
  };

  // Odd - but sometimes needs to be updated. Specifically on the phone call page where
  // the #details is set by the active call module.
  useEffect(() => {
    setPanel(defaultPanel);
  }, [defaultPanel]);

  const showCollapsible = !isMobile && rightPanel?.collapsible;
  // Without a toggle to expand it, a collapsed panel on mobile would be unreachable.
  const isRightPanelCollapsed = !!showCollapsible && rightPanelCollapsed;

  return (
    <Box
      className="two-panel-grid-container"
      display="flex"
      flexDirection="column"
      sx={{
        height: isMobile ? "calc(100vh - 51px)" : PAGE_FRAME_HEIGHT,
        outline: "1px solid #FDFAF7",
      }}
    >
      {isMobile && rightPanel && (
        <Box>
          <StyledHeader>
            <Box display="flex" flexDirection="column" width="100%">
              {panel === Panel.Left ? leftPanel.title : rightPanel?.title}
              {panel === Panel.Left ? leftPanel.subtitle : rightPanel?.subtitle}
            </Box>
            {showHeaderAction && headerAction}
          </StyledHeader>

          {leftPanel?.switchContent && rightPanel?.switchContent && (
            <Box
              sx={{
                display: "flex",
                padding: "12px 16px 4px",
                boxShadow: "0px 2px 8px 0px rgba(151, 93, 51, 0.10)",
              }}
            >
              <ButtonGroup
                sx={{
                  display: "flex",
                  width: "100%",
                  boxShadow: "0px 0px 2px 0px rgba(0, 0, 0, 0.25) inset",
                }}
              >
                <Button
                  sx={{
                    marginRight: "10px",
                    height: "40px",
                    width: "100%",
                    padding: 0,
                  }}
                  variant={
                    panel === Panel.Left ? "buttonGroupSelected" : "buttonGroup"
                  }
                  onClick={() => changePanel(Panel.Left)}
                >
                  {leftPanel.switchContent}
                </Button>
                <Button
                  sx={{
                    height: "40px",
                    width: "100%",
                    padding: 0,
                  }}
                  variant={
                    panel === Panel.Right
                      ? "buttonGroupSelected"
                      : "buttonGroup"
                  }
                  onClick={() => changePanel(Panel.Right)}
                >
                  {rightPanel?.switchContent}
                </Button>
              </ButtonGroup>
            </Box>
          )}
        </Box>
      )}
      <Grid
        container
        spacing={0}
        columnSpacing={isMobile ? "0" : "12px"}
        sx={{
          background: "#FDFAF7",
          height: "100%",
          marginLeft: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <MaybeTransition visible={panel === Panel.Left}>
          <Grid
            item
            xs={12}
            md={isRightPanelCollapsed ? 12 : 8}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              padding: !isDesktop ? "16px !important" : "32px 12px 0",
              borderRadius: isMobile ? "0" : "16px 16px 0 0",
              border: isMobile ? undefined : "1px solid #EAECF0",
              background: "white",
              overflowY: "auto",
              scrollbarWidth: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflowY: "auto",
                scrollbarWidth: "none",
                width: "100%",
                maxWidth: leftPanelMaxWidth,
                alignSelf: "center",
              }}
            >
              {showHeaderAction && (
                <StyledHeader>
                  <Box display="flex" flexDirection="column" width="100%">
                    {leftPanel.subtitle}
                    {leftPanel.title}
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {headerAction}
                    {showCollapsible &&
                      (rightPanel?.customToggleButton ? (
                        rightPanel.customToggleButton(
                          rightPanelCollapsed,
                          toggleRightPanel,
                        )
                      ) : (
                        <IconButton
                          onClick={toggleRightPanel}
                          sx={{
                            width: "32px",
                            height: "32px",
                          }}
                        >
                          {rightPanelCollapsed ? (
                            <ChevronLeft />
                          ) : (
                            <ChevronRight />
                          )}
                        </IconButton>
                      ))}
                  </Box>
                </StyledHeader>
              )}
              {leftPanel.content}
            </Box>
          </Grid>
        </MaybeTransition>
        <MaybeTransition visible={panel === Panel.Right}>
          <Grid
            item
            xs={12}
            md={isRightPanelCollapsed ? 0 : 4}
            sx={{
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              ...rightPanel?.sx,
            }}
          >
            {rightPanel && !isRightPanelCollapsed && (
              <Box
                sx={{
                  borderTop: "1px solid #EAEBEC",
                  borderLeft: "1px solid #EAEBEC",
                  borderRadius: isMobile ? 0 : "16px 0 0 0",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {!isMobile && (rightPanel.title || rightPanel.subtitle) && (
                  <StyledHeader>
                    <Box display="flex" flexDirection="column" width="100%">
                      {rightPanel.title}
                      {rightPanel.subtitle}
                    </Box>
                  </StyledHeader>
                )}
                <Box
                  sx={{
                    background: "white",
                    height: "100%",
                  }}
                >
                  {rightPanel.content}
                </Box>
              </Box>
            )}
          </Grid>
        </MaybeTransition>
      </Grid>
    </Box>
  );
};
